import { useApi, useGlobalContext } from "@hooks";
import { Api } from "@Shared/schemas";
import { PlaylistResponse } from "@Shared/schemas/playlists/playlistSchemas";
import { ScenarioResponse } from "@Shared/schemas/scenarios/scenarioSchemas";
import { debounce } from "lodash-es";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

interface PlaylistDropdownContextType {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedPlaylists: Map<number, PlaylistResponse>;
  togglePlaylist: (playlist: PlaylistResponse) => void;
  playlists: PlaylistResponse[];
  isLoading: boolean;
  scenarios: ScenarioResponse[];
  onClose: () => void;
  showPlaylistDialog: boolean;
  handleCreateNewPlaylist: () => void;
  handleDialogClose: (refresh?: boolean) => void;
  clearSelection: () => void;
  handleSearchQueryChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fetchPlaylists: (query: string) => void;
}

const PlaylistDropdownContext = createContext<PlaylistDropdownContextType | undefined>(undefined);

interface PlaylistDropdownProviderProps {
  children: React.ReactNode;
  scenarios: ScenarioResponse[];
  onClose: () => void;
  isOpen: boolean;
}

export const PlaylistDropdownProvider = ({ children, scenarios, onClose, isOpen }: PlaylistDropdownProviderProps) => {
  const { globalState } = useGlobalContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [showPlaylistDialog, setShowPlaylistDialog] = useState(false);
  const [selectedPlaylists, setSelectedPlaylists] = useState<Map<number, PlaylistResponse>>(new Map());
  const [playlists, setPlaylists] = useState<PlaylistResponse[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const accountId = globalState.account?.id;

  const { call: getPlaylists, loading: isLoading } = useApi<typeof Api.Playlists.GetPlaylists>({
    api: Api.Playlists.GetPlaylists,
    onSuccess: response => {
      setPlaylists(response.data);
    },
  });

  const fetchPlaylists = useCallback(
    (query: string) => {
      if (!accountId) return;
      getPlaylists({
        unpublished_allowed: true,
        account_id: accountId,
        limit: query ? undefined : 5,
        order_by: query ? undefined : [{ field: "created_at", direction: "desc" }],
      });
    },
    [accountId, getPlaylists]
  );

  const debouncedSearch = useMemo(() => debounce(fetchPlaylists, 300), [fetchPlaylists]);

  useEffect(() => {
    if (isOpen && isInitialLoad) {
      fetchPlaylists("");
      setIsInitialLoad(false);
    }
  }, [isOpen, fetchPlaylists, isInitialLoad]);

  useEffect(() => {
    if (!isOpen) {
      setIsInitialLoad(true);
      setSearchQuery("");
      setPlaylists([]);
      setSelectedPlaylists(new Map());
    }
  }, [isOpen]);

  const handleCreateNewPlaylist = () => {
    setShowPlaylistDialog(true);
  };

  const togglePlaylist = (playlist: PlaylistResponse) => {
    setSelectedPlaylists(prev => {
      const newMap = new Map(prev);
      if (newMap.has(playlist.id)) {
        newMap.delete(playlist.id);
      } else {
        newMap.set(playlist.id, playlist);
      }
      return newMap;
    });
  };

  const handleDialogClose = (refresh?: boolean) => {
    setShowPlaylistDialog(false);
    if (refresh) {
      fetchPlaylists(searchQuery);
    }
  };

  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const clearSelection = () => {
    setSelectedPlaylists(new Map());
  };

  const value = useMemo(
    () => ({
      searchQuery,
      setSearchQuery,
      selectedPlaylists,
      togglePlaylist,
      playlists,
      isLoading,
      scenarios,
      onClose,
      showPlaylistDialog,
      handleCreateNewPlaylist,
      handleDialogClose,
      clearSelection,
      handleSearchQueryChange,
      fetchPlaylists,
    }),
    [
      searchQuery,
      selectedPlaylists,
      playlists,
      isLoading,
      scenarios,
      onClose,
      showPlaylistDialog,
      handleSearchQueryChange,
      fetchPlaylists,
    ]
  );

  return <PlaylistDropdownContext.Provider value={value}>{children}</PlaylistDropdownContext.Provider>;
};

export const usePlaylistDropdown = () => {
  const context = useContext(PlaylistDropdownContext);
  if (context === undefined) {
    throw new Error("usePlaylistDropdown must be used within a PlaylistDropdownProvider");
  }
  return context;
};
